import { useState } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import { extractErrorMessage } from '../utils/apitools'
import {
  DashboardConfig,
  GenerateDashboardStructureRequest,
  GenerateDashboardStructureResponse,
} from '../types/gotypes'

/**
 * The sessionStorage key the generated config is handed to the layout builder
 * through (#971 Phase 3). A full DashboardConfig doesn't fit in a query param
 * the way a template id does, so DashboardCreatePage stashes it here and
 * navigates with `generated: '1'`; DashboardBuilderPage reads it back in its
 * state initializer — the same synchronous seed timing as a system template's
 * `build(job)`. The key is only ever read when the `generated` param is
 * present, so a stale value can't leak into other creation paths.
 */
export const GENERATED_CONFIG_STORAGE_KEY = 'platinum.dashboardBuilder.generatedConfig'

interface UseGenerateDashboardStructureProps {
  customer: string
}

interface UseGenerateDashboardStructureReturn {
  /** Resolves to the structure-only config, or null on failure (error is set). */
  generate: (req: GenerateDashboardStructureRequest) => Promise<DashboardConfig | null>
  isGenerating: boolean
  error: string | null
  clearError: () => void
}

/**
 * Agent-assisted dashboard creation (#971 Phase 3): one POST to
 * `/dashboards/:customer/generate-structure`, returning a structure-and-shells
 * -only DashboardConfig for the layout builder to seed from. Deliberately NOT
 * snackbar-driven: the create page renders `error` inline with the user's
 * inputs preserved, so a model failure is always retryable, never a dead end.
 */
export const useGenerateDashboardStructure = ({
  customer,
}: UseGenerateDashboardStructureProps): UseGenerateDashboardStructureReturn => {
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generate = async (req: GenerateDashboardStructureRequest): Promise<DashboardConfig | null> => {
    setIsGenerating(true)
    setError(null)
    try {
      const res = await axios.post<GenerateDashboardStructureResponse>(
        `${API_BASE_URL}/dashboards/${customer}/generate-structure`,
        req,
      )
      return res.data.config
    } catch (err) {
      setError(extractErrorMessage(err))
      return null
    } finally {
      setIsGenerating(false)
    }
  }

  return {
    generate,
    isGenerating,
    error,
    clearError: () => setError(null),
  }
}

export default useGenerateDashboardStructure
